import React, { useMemo, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Stack } from '@mui/joy';
import {InputLabel, MenuItem, Select as MuiSelect, TextField, InputAdornment, IconButton, Popper, Paper, ClickAwayListener} from '@mui/material';import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ContactsIcon from '@mui/icons-material/Contacts';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const StyledMuiSelect = styled(MuiSelect)(({ theme }) => ({
  '& label': {
    color: theme.palette.gray2.main,
    '&.Mui-focused': {
      color: theme.palette.green.main,
    },
  },
  '&.MuiInputBase-root': {
    fontWeight: 500,
    height: '50px',
    boxShadow: theme.shadows[0],
    borderRadius: '10px',
    backgroundColor: theme.palette.white.main,
    color: theme.palette.gray1.main,
    '&:focus-visible': {
      outline: 'none',
    },
    '& fieldset': {
      border: 'none',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.white.main,
    },
    ':hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: '0px',
    },
    ':hover': {
      backgroundColor: theme.palette.white.hover,
    },
    '&::before': {
      borderBottom: '0px',
    },
    '&::after': {
      borderBottom: '0px',
    },
  },
  '& .MuiSelect-select': {
    '&:focus-visible': {
      outline: 'none',
    },
  },
}));
const StyledCollapsedSelect = styled(MuiSelect)(({ theme }) => ({
  '&.MuiInputBase-root': {
    height: '50px',
    width: '50px',
    boxShadow: theme.shadows[0],
    borderRadius: '10px',
    backgroundColor: theme.palette.white.main,
    color: theme.palette.gray1.main,
    '&:focus-visible': {
      outline: 'none',
    },
    '& fieldset': {
      border: 'none',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.white.main,
    },
    ':hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: '0px',
    },
    ':hover': {
      backgroundColor: theme.palette.white.hover,
    },
    '&::before': {
      borderBottom: '0px',
    },
    '&::after': {
      borderBottom: '0px',
    },
  },
  '& .MuiSelect-select': {
    fontSize: '0px',
    padding: '10px',
    '&:focus-visible': {
      outline: 'none',
    },
  },
  '& .MuiSelect-iconOutlined': {
    position: 'absolute',
    left: 13,
    top: 25,
  },
}));
const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  fontSize: '12px',
  paddingLeft: '12px',
}));
const StyledArrowIcon = styled(ExpandMoreRoundedIcon)(({ theme }) => ({
  fill: theme.palette.green.main,
}));
const StyledGroupsIcon = styled(GroupsRoundedIcon)(({ theme }) => ({
  fill: theme.palette.green.main,
  width: '30px',
  height: '30px',
}));

const StyledFilterInput = styled(TextField)(({ theme }) => ({
  '&.MuiTextField-root': {
    height: '50px',
    '&:focus-visible': {
      outline: 'none',
    },
  },
  '& .MuiInputBase-root': {
    boxShadow: theme.shadows[0],
    borderRadius: '10px',
    backgroundColor: theme.palette.white.main,
    color: theme.palette.gray1.main,
    '&.Mui-focused': {
      backgroundColor: theme.palette.white.main,
    },
    ':hover': {
      backgroundColor: theme.palette.white.hover,
    },
    '&:focus-visible': {
      outline: 'none',
    },
  },
  '& fieldset': {
    border: 'none',
  },
}));


const SelectComponent = (props) => {
  const { collapsed } = props;

  return collapsed ? (
    <StyledCollapsedSelect
      renderValue={() => {
        return <StyledGroupsIcon />;
      }}
      {...props}
    />
  ) : (
    <StyledMuiSelect {...props} />
  );
};

export const Select = (props) => {
  const {id, label, options, value, setValue, collapsed, labelSX, stackSX, customRender, filterable, filterPlaceholder, clearValue,} = props;

  // With clearValue set (e.g. "All Users"), a selection other than it is shown in the
  // input and gets an X that resets back to clearValue in one click
  const hasSelection = clearValue !== undefined && value && value !== clearValue;

  const [filterText, setFilterText] = useState('');
  const filterAnchorRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpenMenu = () => setAnchorEl(filterAnchorRef.current);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleSelectOption = (option) => {
    setValue(option);
    setFilterText('');
    handleCloseMenu();
  };

  const { searchResults, otherOptions, allOptions } = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const all = options ?? [];

    if (!q) return { searchResults: [], otherOptions: [], allOptions: all };

    const matches = [];
    const others = [];

    all.forEach((opt) => {
      const s = String(opt).toLowerCase();
      if (s.includes(q)) matches.push(opt);
      else others.push(opt);
    });

    return { searchResults: matches, otherOptions: others, allOptions: all };
  }, [filterText, options]);

  const menuItems = options.map((option) => {
    return (
      <MenuItem key={option} value={option}>
        {customRender ? customRender(option) : option}
      </MenuItem>
    );
  });

  const handleEnterSelect = (e) => {
    if (e.key === 'Escape') {
      handleCloseMenu();
      return;
    }

    const q = filterText.trim();
    const list = q ? searchResults : allOptions;

    if (e.key === 'Enter' && list.length > 0) {
      handleSelectOption(list[0]);
    }
  };

  if (filterable && !collapsed) {
    return (
      <Stack sx={stackSX}>
        {label ? (
          <StyledInputLabel id={`${id}-label`} sx={labelSX}>
            {label}
          </StyledInputLabel>
        ) : null}

        <div ref={filterAnchorRef}>
          <StyledFilterInput
            value={filterText}
            placeholder={hasSelection ? String(value) : filterPlaceholder || 'Search users...'}
            onFocus={handleOpenMenu}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={handleEnterSelect}
            sx={hasSelection ? { '& input::placeholder': { opacity: 1, fontWeight: 500 } } : undefined}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ marginLeft: '10px' }}>
                  <ContactsIcon sx={{ color: (theme) => theme.palette.green.main }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: '10px' }}>
                  {hasSelection ? (
                    <IconButton
                      aria-label={`Clear, show ${clearValue}`}
                      title={`Back to ${clearValue}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectOption(clearValue)}
                    >
                      <CloseRoundedIcon sx={{ color: (theme) => theme.palette.gray2.main }} />
                    </IconButton>
                  ) : null}
                  <IconButton
                    edge="end"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleOpenMenu}
                  >
                    <StyledArrowIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <Popper
          open={menuOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
          ]}
          sx={{ zIndex: 1300 }}
        >
          <ClickAwayListener
            onClickAway={(e) => {
              const anchor = filterAnchorRef.current;
              if (anchor && anchor.contains(e.target)) return;
              handleCloseMenu();
            }}
          >
            <Paper
              sx={{
                width: filterAnchorRef.current
                  ? filterAnchorRef.current.clientWidth
                  : undefined,
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: 3,
              }}
            >
              {filterText.trim() ? (
                <>
                  <MenuItem disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                    Search Results
                  </MenuItem>

                  {searchResults.length === 0 ? (
                    <MenuItem disabled>No users found</MenuItem>
                  ) : (
                    searchResults.map((option, idx) => (
                      <MenuItem
                        key={`sr-${String(option)}-${idx}`}
                        onClick={() => handleSelectOption(option)}
                      >
                        {customRender ? customRender(option) : option}
                      </MenuItem>
                    ))
                  )}

                  <MenuItem disabled sx={{ opacity: 0.7, fontSize: 12 }}>
                    Other options
                  </MenuItem>
                </>
              ) : null}

              {filterText.trim()
                ? otherOptions.map((option, idx) => (
                    <MenuItem
                      key={`other-${String(option)}-${idx}`}
                      onClick={() => handleSelectOption(option)}
                    >
                      {customRender ? customRender(option) : option}
                    </MenuItem>
                  ))
                : allOptions.map((option, idx) => (
                    <MenuItem
                      key={`all-${String(option)}-${idx}`}
                      onClick={() => handleSelectOption(option)}
                    >
                      {customRender ? customRender(option) : option}
                    </MenuItem>
                  ))}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Stack>
    );
  }

  return (
    <Stack sx={stackSX}>
      {label && !collapsed ? (
        <StyledInputLabel id={`${id}-label`} sx={labelSX}>
          {label}
        </StyledInputLabel>
      ) : null}
      <SelectComponent
        labelId={`${id}-label`}
        id={id}
        label={label}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        collapsed={collapsed}
        IconComponent={StyledArrowIcon}
        {...props}
      >
        {menuItems}
      </SelectComponent>
    </Stack>
  );
};